'use client';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import _ from 'lodash';
import { useCallback } from 'react';

import { ScreenContainer, InputScreen } from 'src/components/flow';
import { InfomarkerIcon } from 'src/components/screens/shared/infomarker-icon';
import { Input } from 'src/lib/constants';
import {
  stringifyAnswerValue,
  updateQuizAnswerAnalytics,
} from 'src/utils/analytics/utils';
import { getInputLabel } from 'src/utils/inputs/labels';
import SpendInput from '../spend-input/spend-input';
import useIsSpendValid from '../spend-input/useIsSpendValid';

import type { FlowScreenProps } from './types';
import { useAppSelector } from 'src/redux/hooks';
import { getExperiments } from 'src/redux/selectors';
import { useSegmentPageAction } from 'src/utils/analytics/hooks';
import { PageActionName, PageActionType } from 'src/utils/analytics/constants';

const INPUT_ID = 'monthly-spend-input';

const MonthlySpendScreen: React.FC<FlowScreenProps> = ({
  inputs,
  updateInput,
  pageNumber,
  trackEvent,
  nerdyTip = '',
}) => {
  const trackPageAction = useSegmentPageAction();
  const experiments = useAppSelector(getExperiments);
  const isValid = useIsSpendValid(inputs[Input.MonthlySpend]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const onChange = useCallback(
    (value: number) => {
      updateInput({ name: Input.MonthlySpend, value });
    },
    [updateInput]
  );

  return (
    <ScreenContainer
      nerdyTip={nerdyTip}
      primaryCta={{
        text: 'Next',
        isDisabled: _.isNil(inputs[Input.MonthlySpend]) || !isValid,
        onClick: async () => {
          const { eventType, payload } = updateQuizAnswerAnalytics(
            inputs[Input.MonthlySpend],
            Input.MonthlySpend,
            pageNumber
          );
          trackPageAction({
            actionName: PageActionName.MonthlySpend,
            actionType: PageActionType.Input,
            actionInput: stringifyAnswerValue(inputs[Input.MonthlySpend]),
          });
          trackEvent(eventType, payload);
        },
      }}
    >
      <InputScreen
        headline={
          <>
            <Typography
              variant="titleSmall"
              id="monthly-spend-label"
              component="label"
              htmlFor={INPUT_ID}
            >
              {getInputLabel(Input.MonthlySpend, inputs, experiments)}
            </Typography>
            {nerdyTip && <InfomarkerIcon />}
          </>
        }
      >
        <Box display="flex" flexDirection="column" gap={4}>
          <SpendInput
            autoFocus={!isMobile}
            id={INPUT_ID}
            isValid={isValid}
            onChange={onChange}
            value={inputs[Input.MonthlySpend]}
            sx={{
              [theme.breakpoints.up('sm')]: {
                width: '25%',
              },
              [theme.breakpoints.down('sm')]: {
                width: '100%',
              },
            }}
          />
        </Box>
      </InputScreen>
    </ScreenContainer>
  );
};

export default MonthlySpendScreen;
